import { createContext, useContext, useMemo, Fragment } from "react";
import { Select as SelectPrimitive } from "@base-ui/react/select";
import { clsx } from "clsx";
import styles from "./Select.module.css";
import list from "./ListPopup.module.css";

interface SelectItemDef<Value> {
	label: React.ReactNode
	value: Value
	disabled?: boolean
}

interface SelectGroupDef<Value> {
	/** Group heading, rendered as `Select.GroupLabel`. Omit for an unlabelled group. */
	label?: React.ReactNode
	items: ReadonlyArray<SelectItemDef<Value>>
}

type SelectItemsList<Value> = ReadonlyArray<SelectItemDef<Value> | SelectGroupDef<Value>>

function isGroupDef<Value>(
	entry: SelectItemDef<Value> | SelectGroupDef<Value>,
): entry is SelectGroupDef<Value> {
	return "items" in entry && !("value" in entry)
}

const SelectItemsContext = createContext<SelectItemsList<unknown> | undefined>(undefined)

function SelectRoot<Value, Multiple extends boolean | undefined = false>({
																			 items,
																			 ...props
																		 }: Omit<SelectPrimitive.Root.Props<Value, Multiple>, "items"> & {
	items?: SelectItemsList<Value>
}) {
	const flatItems = useMemo(
		() => items?.flatMap((entry) => (isGroupDef(entry) ? entry.items : entry)),
		[items],
	)
	return (
		<SelectItemsContext.Provider value={items}>
			<SelectPrimitive.Root data-slot="select" items={flatItems} {...props} />
		</SelectItemsContext.Provider>
	)
}


function SelectTrigger({
						   className,
						   placeholder,
						   children,
						   ...props
					   }: SelectPrimitive.Trigger.Props & {
	/** Shown while no value is selected. Ignored when `children` is passed. */
	placeholder?: React.ReactNode
}) {
	return (
		<SelectPrimitive.Trigger
			data-slot="select-trigger"
			className={clsx(styles.trigger, className)}
			{...props}
		>
			{children ?? (
				<>
					<SelectValue placeholder={placeholder}/>
					<SelectIcon/>
				</>
			)}
		</SelectPrimitive.Trigger>
	)
}

function SelectIcon({className, children, ...props}: SelectPrimitive.Icon.Props) {
	return (
		<SelectPrimitive.Icon
			data-slot="select-icon"
			className={clsx(styles.icon, className)}
			{...props}
		>
			{children ?? (
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 4.95 10">
					<polygon points="1.41 4.67 2.48 3.18 3.54 4.67 1.41 4.67"/>
					<polygon points="3.54 5.33 2.48 6.82 1.41 5.33 3.54 5.33"/>
				</svg>
			)}
		</SelectPrimitive.Icon>
	)
}

/**
 * Text of the selected item — resolved from Root's `items` when given, the raw
 * value otherwise — or the placeholder. Ellipsizes when the trigger is narrow.
 * Rendered by `Select.Trigger` automatically; only needed for custom triggers.
 */
function SelectValue({className, ...props}: SelectPrimitive.Value.Props) {
	return (
		<SelectPrimitive.Value
			data-slot="select-value"
			className={clsx(styles.value, className)}
			{...props}
		/>
	)
}


function SelectPopup({className, children, ...props}: SelectPrimitive.Popup.Props) {
	return (
		<SelectPrimitive.Portal data-slot="select-portal">
			<SelectPrimitive.Positioner
				data-slot="select-positioner"
				className={list.positioner}
				alignItemWithTrigger={false}
				sideOffset={2}
				align="start"
			>
				<SelectPrimitive.Popup
					data-slot="select-popup"
					className={clsx(list.popup, className)}
					{...props}
				>
					<SelectPrimitive.List data-slot="select-list">
						{children}
					</SelectPrimitive.List>
				</SelectPrimitive.Popup>
			</SelectPrimitive.Positioner>
		</SelectPrimitive.Portal>
	)
}

/**
 * The dropdown, rendered entirely from Root's `items` — the default way to
 * build a select: `Root` + `Trigger` + `Items`, no mapping at the call site.
 * Group entries (`{ label, items }`) become `Select.Group`s with a
 * `.dropdown-header`-style label, separated from their neighbours by a
 * divider. For popup content the items list can't express (custom markup,
 * per-item icons), compose `Select.Popup` with `Select.Item`s instead.
 */
function SelectItems(props: Omit<SelectPrimitive.Popup.Props, "children">) {
	const items = useContext(SelectItemsContext)
	if (items === undefined) {
		throw new Error("<Select.Items> requires the `items` prop on <Select.Root>.")
	}
	return (
		<SelectPopup {...props}>
			{items.map((entry, index) => {
				const previous = items[index - 1]
				const separated = previous !== undefined && (isGroupDef(entry) || isGroupDef(previous))
				return (
					<Fragment key={index}>
						{separated && <SelectSeparator/>}
						{isGroupDef(entry) ? (
							<SelectGroup>
								{entry.label != null && <SelectGroupLabel>{entry.label}</SelectGroupLabel>}
								{entry.items.map((item, itemIndex) => (
									<SelectItem key={itemIndex} value={item.value} disabled={item.disabled}>
										{item.label}
									</SelectItem>
								))}
							</SelectGroup>
						) : (
							<SelectItem value={entry.value} disabled={entry.disabled}>
								{entry.label}
							</SelectItem>
						)}
					</Fragment>
				)
			})}
		</SelectPopup>
	)
}

/**
 * One option. Give it a `value` and its text as children (also used for
 * typeahead matching; pass `label` when the children aren't plain text). The
 * selected item shows inverted on brand primary like an active Limitless
 * dropdown item — no checkmark, matching the app's menus.
 */
function SelectItem({className, children, ...props}: SelectPrimitive.Item.Props) {
	return (
		<SelectPrimitive.Item
			data-slot="select-item"
			className={clsx(list.item, className)}
			{...props}
		>
			<SelectPrimitive.ItemText
				data-slot="select-item-text"
				className={list.itemText}
			>
				{children}
			</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	)
}

/** Groups related items (behaviour only) — label the group with `Select.GroupLabel`. */
function SelectGroup({className, ...props}: SelectPrimitive.Group.Props) {
	return <SelectPrimitive.Group data-slot="select-group" className={className} {...props} />
}

/** Heading for a `Select.Group`, styled like the Limitless `.dropdown-header` (11px, uppercase, muted). */
function SelectGroupLabel({className, ...props}: SelectPrimitive.GroupLabel.Props) {
	return (
		<SelectPrimitive.GroupLabel
			data-slot="select-group-label"
			className={clsx(list.groupLabel, className)}
			{...props}
		/>
	)
}

/** Thin divider between items or groups, like the `.dropdown-menu` `.divider`. */
function SelectSeparator({className, ...props}: SelectPrimitive.Separator.Props) {
	return (
		<SelectPrimitive.Separator
			data-slot="select-separator"
			className={clsx(list.separator, className)}
			{...props}
		/>
	)
}

const Select = {
	Root: SelectRoot,
	Trigger: SelectTrigger,
	Value: SelectValue,
	Popup: SelectPopup,
	Items: SelectItems,
	Item: SelectItem,
	Group: SelectGroup,
	GroupLabel: SelectGroupLabel,
	Separator: SelectSeparator,
};

export {Select};
export type {SelectItemDef, SelectGroupDef, SelectItemsList};
